import React, { useMemo } from 'react';
import { Box, Typography } from '@lucid/lego';
import type { OrderPreference } from '../../../../../../api/services/lupp/order/importer/dto.importer';
import type { CheckoutItem } from '../types';
import VehicleCard from '../VehicleCard/VehicleCard';
import styles from './VehicleList.module.scss';

const resolveExteriorColor = (ctx: Record<string, unknown> | undefined): string => {
  if (ctx?.exteriorColor) return ctx.exteriorColor as string;
  const options = ctx?.vehicleOptions as Array<{ type: string; name: string }> | undefined;
  return options?.find((o) => o.type === 'LUCID_EXT_COLOR')?.name ?? '';
};

const toGroupKey = (item: CheckoutItem): string => {
  const ctx = item.itemContext as Record<string, unknown> | undefined;
  const model = (ctx?.model as string) ?? '';
  const trim = (ctx?.trim as string) ?? '';
  const year = (ctx?.modelYear as string) ?? (ctx?.year as string) ?? '';
  const color = resolveExteriorColor(ctx);
  if (model || trim || year || color) {
    return [model, trim, year, color].join('|');
  }
  return item.itemId ?? item.itemCode ?? 'unknown';
};

type ItemGroup = { groupId: string; items: CheckoutItem[] };

const groupItems = (items: CheckoutItem[]): ItemGroup[] => {
  const map = new Map<string, CheckoutItem[]>();
  for (const item of items) {
    const key = toGroupKey(item);
    const existing = map.get(key);
    if (existing) {
      existing.push(item);
    } else {
      map.set(key, [item]);
    }
  }
  return Array.from(map.entries()).map(([groupId, groupItems]) => ({ groupId, items: groupItems }));
};

type VehicleListLabels = Record<string, string>;

export type VehicleListProps = {
  theme: 'dark' | 'light';
  labels?: VehicleListLabels;
  isLoading: boolean;
  items: CheckoutItem[];
  locale?: string;
  expandedItems: Record<string, boolean>;
  onToggle: (id: string) => void;
  customerId: string;
  orderPrefs: Record<string, OrderPreference>;
  onOrderPreferenceChange: (itemId: string, unitIndex: number, value: OrderPreference) => void;
  onAllItemsDeleted?: () => void;
};

/**
 * Renders the list of vehicle cards in the cart, including loading and empty states.
 * @component
 * @param {VehicleListProps} props
 * @returns {JSX.Element}
 * @example
 * <VehicleList theme="light" isLoading={false} items={items} currencyCode="USD" expandedItems={{}} onToggle={() => {}} customerId="123" />
 */
const VehicleList: React.FC<VehicleListProps> = ({
  theme,
  labels,
  isLoading,
  items,
  locale,
  expandedItems,
  onToggle,
  customerId,
  orderPrefs,
  onOrderPreferenceChange,
  onAllItemsDeleted,
}) => {
  const groups = useMemo(() => groupItems(items), [items]);

  return (
    <Box
      className={styles.vehicleList}
      data-testid="checkout-vehicle-list"
      background="white"
      pl={16}
      pr={16}
      pb={24}
    >
      {isLoading && (
        <Box pt={24} pb={24}>
          <Typography variant="body" size="small" color="gray-3" theme={theme}>
            Loading...
          </Typography>
        </Box>
      )}
      {!isLoading && items.length === 0 && (
        <Box pt={24} pb={24}>
          <Typography
            variant="body"
            size="small"
            color="gray-3"
            theme={theme}
            data-testid="checkout-empty-state"
          >
            {labels?.emptyCart ?? 'Your cart is empty.'}
          </Typography>
        </Box>
      )}
      {groups.map(({ groupId, items: groupItems }) => (
        <VehicleCard
          key={groupId}
          items={groupItems}
          locale={locale}
          theme={theme}
          isExpanded={expandedItems[groupId] ?? false}
          onToggle={() => onToggle(groupId)}
          customerId={customerId}
          orderPrefs={orderPrefs}
          onOrderPreferenceChange={onOrderPreferenceChange}
          onAllItemsDeleted={onAllItemsDeleted}
          labels={labels}
        />
      ))}
    </Box>
  );
};

export default VehicleList;
